import { useCallback, useEffect, useState } from 'react';
import { deepEqual, generateTempId } from '../utils/crudHelpers';
import { showErrorToast, describeError } from '../components/ErrorToast';
import { t } from '../i18n/t';

export interface RowState<T> {
  data: T;
  tempId: string;
  status: 'added' | 'modified' | 'unchanged';
  isDeleted: boolean;
  original?: T;
}

export interface UseCrudGridOptions<T> {
  getRows: () => Promise<T[]>;
  saveBatch: (added: T[], modified: T[], deletedIds: unknown[]) => Promise<T[]>;
  idField: keyof T;
  newRowDefaults?: () => Partial<T>;
  // false = "not fetched yet": no load (initial or refresh) until it flips true.
  enabled?: boolean;
}

export interface UseCrudGridResult<T> {
  rowStates: RowState<T>[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  hasChanges: boolean;
  addRow: (defaults?: Partial<T>) => string;
  updateRow: (tempId: string, nextData: T) => void;
  toggleDelete: (tempId: string) => void;
  save: () => Promise<void>;
  cancel: () => void;
  refresh: () => Promise<void>;
}

function rowsToStates<T>(rows: T[]): RowState<T>[] {
  return rows.map((row) => ({
    data: row,
    tempId: generateTempId(),
    status: 'unchanged' as const,
    isDeleted: false,
    original: row,
  }));
}

export function useCrudGrid<T extends object>(
  options: UseCrudGridOptions<T>,
): UseCrudGridResult<T> {
  const { getRows, saveBatch, idField, newRowDefaults, enabled = true } = options;

  const [rowStates, setRowStates] = useState<RowState<T>[]>([]);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await getRows();
      setRowStates(rowsToStates(rows));
    } catch (err) {
      showErrorToast(err, { title: t('errorToast.titleLoad') });
      setError(describeError(err).body);
    } finally {
      setLoading(false);
    }
  }, [getRows, enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await getRows();
        if (!cancelled) setRowStates(rowsToStates(rows));
      } catch (err) {
        if (!cancelled) {
          showErrorToast(err, { title: t('errorToast.titleLoad') });
          setError(describeError(err).body);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getRows, enabled]);

  const addRow = useCallback(
    (defaults?: Partial<T>) => {
      const merged = { ...(newRowDefaults?.() ?? {}), ...(defaults ?? {}) } as T;
      const tempId = generateTempId();
      setRowStates((prev) => [
        ...prev,
        {
          data: merged,
          tempId,
          status: 'added',
          isDeleted: false,
        },
      ]);
      return tempId;
    },
    [newRowDefaults],
  );

  const updateRow = useCallback(
    (tempId: string, nextData: T) => {
      setRowStates((prev) =>
        prev.map((row) => {
          if (row.tempId !== tempId) return row;
          if (row.status === 'added') {
            return { ...row, data: nextData };
          }
          const nextStatus: RowState<T>['status'] =
            row.original && deepEqual(nextData, row.original) ? 'unchanged' : 'modified';
          return { ...row, data: nextData, status: nextStatus };
        }),
      );
    },
    [],
  );

  const toggleDelete = useCallback((tempId: string) => {
    setRowStates((prev) => {
      const target = prev.find((r) => r.tempId === tempId);
      if (!target) return prev;
      // Marking an 'added' row deleted: drop it entirely (never existed on server).
      if (target.status === 'added' && !target.isDeleted) {
        return prev.filter((r) => r.tempId !== tempId);
      }
      return prev.map((r) =>
        r.tempId === tempId ? { ...r, isDeleted: !r.isDeleted } : r,
      );
    });
  }, []);

  const hasChanges = rowStates.some(
    (r) => r.status !== 'unchanged' || r.isDeleted,
  );

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const added = rowStates
        .filter((r) => r.status === 'added' && !r.isDeleted)
        .map((r) => r.data);
      const modified = rowStates
        .filter((r) => r.status === 'modified' && !r.isDeleted)
        .map((r) => r.data);
      const deletedIds = rowStates
        .filter((r) => r.isDeleted && r.status !== 'added')
        .map((r) => r.data[idField]);

      const fresh = await saveBatch(added, modified, deletedIds);
      setRowStates(rowsToStates(fresh));
    } catch (err) {
      showErrorToast(err, { title: t('errorToast.titleSave') });
    } finally {
      setSaving(false);
    }
  }, [rowStates, saveBatch, idField]);

  const cancel = useCallback(() => {
    setRowStates((prev) =>
      prev
        .filter((r) => r.status !== 'added')
        .map((r) => ({
          ...r,
          data: r.original ?? r.data,
          status: 'unchanged' as const,
          isDeleted: false,
        })),
    );
    setError(null);
  }, []);

  return {
    rowStates,
    loading,
    saving,
    error,
    hasChanges,
    addRow,
    updateRow,
    toggleDelete,
    save,
    cancel,
    refresh,
  };
}
